const GENERIC_PERMISSION_ERRORS = new Set(['Forbidden', 'forbidden'])
const GENERIC_AUTH_ERRORS = new Set(['Unauthorized', 'unauthorized'])
const LOWER_MACHINE_CODE = /^[a-z][a-z0-9_]*$/
const UPPER_MACHINE_CODE = /^[A-Z][A-Z0-9_]*$/
const SENTENCE_TERMINATOR = /[.!?]$/

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringField(body: Record<string, unknown>, field: string): string | null {
  const value = body[field]
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function normalizeMachineCode(value: string): string | null {
  const trimmed = value.trim()
  if (LOWER_MACHINE_CODE.test(trimmed) || UPPER_MACHINE_CODE.test(trimmed)) {
    return trimmed.toUpperCase()
  }
  return null
}

function inferPermissionCode(message: string): string | null {
  if (message.includes('permission to view tasks in this department')) {
    return 'TASK_VIEW_DEPARTMENT_PERMISSION_REQUIRED'
  }
  if (message.includes('permission to create tasks in this department')) {
    return 'TASK_CREATE_DEPARTMENT_PERMISSION_REQUIRED'
  }
  if (message.includes('permission to edit tasks in this department')) {
    return 'TASK_EDIT_DEPARTMENT_PERMISSION_REQUIRED'
  }
  if (message.includes('permission to delete tasks in this department')) {
    return 'TASK_DELETE_DEPARTMENT_PERMISSION_REQUIRED'
  }
  if (message.includes('permission to assign tasks in this department')) {
    return 'TASK_ASSIGN_DEPARTMENT_PERMISSION_REQUIRED'
  }
  if (message.includes('permission to approve tasks in this department')) {
    return 'TASK_APPROVE_DEPARTMENT_PERMISSION_REQUIRED'
  }
  if (message.includes('permission to edit task ') && message.includes(' in this department')) {
    return 'TASK_EDIT_FIELD_DEPARTMENT_PERMISSION_REQUIRED'
  }
  return null
}

function sentence(value: string): string {
  return SENTENCE_TERMINATOR.test(value) ? value : `${value}.`
}

function displayMessageFromBody(body: Record<string, unknown>): string | null {
  const message = stringField(body, 'message') ?? stringField(body, 'detail')
  if (message) {
    return message
  }

  const error = stringField(body, 'error')
  if (!error) {
    return null
  }
  if (GENERIC_PERMISSION_ERRORS.has(error) || GENERIC_AUTH_ERRORS.has(error)) {
    return null
  }
  if (normalizeMachineCode(error)) {
    return null
  }
  return error
}

function fallbackMessage(status: number | undefined, fallback: string): string {
  if (status === 401) {
    return 'Your session expired. Sign in again to continue.'
  }
  if (status === 403) {
    return 'You do not have permission to perform this action.'
  }
  return fallback
}

function fallbackFix(status: number | undefined): string | null {
  if (status === 403) {
    return 'Ask a workspace admin to check your role and access settings.'
  }
  return null
}

function fallbackCode(status: number | undefined): string | undefined {
  if (status === 401) {
    return 'SESSION_EXPIRED'
  }
  if (status === 403) {
    return 'PERMISSION_DENIED'
  }
  return undefined
}

export function extractApiErrorCode(body: unknown, status?: number): string | undefined {
  if (isRecord(body)) {
    const explicitCode = stringField(body, 'code')
    if (explicitCode) {
      return explicitCode
    }

    const error = stringField(body, 'error')
    if (error) {
      const permissionCode = inferPermissionCode(error)
      if (permissionCode) {
        return permissionCode
      }

      const machineCode = normalizeMachineCode(error)
      if (machineCode) {
        return machineCode
      }
    }
  }

  return fallbackCode(status)
}

export function buildApiErrorMessage(body: unknown, fallback: string, status?: number): string {
  const record = isRecord(body) ? body : null
  const message = record ? displayMessageFromBody(record) : null
  const fix = record ? stringField(record, 'fix') : null
  const code = extractApiErrorCode(body, status)

  const parts = [sentence(message ?? fallbackMessage(status, fallback))]
  const nextStep = fix ?? fallbackFix(status)
  if (nextStep) {
    parts.push(sentence(nextStep))
  }
  if (code) {
    parts.push(`Code: ${code}`)
  }

  return parts.join(' ')
}

export async function responseErrorMessage(response: Response, fallback: string): Promise<string> {
  let body: unknown = null
  try {
    body = await response.json()
  } catch {
    body = null
  }

  return buildApiErrorMessage(body, fallback, response.status)
}
